import React from 'react';
import { Button, Confirmation, Dropdown, Loader } from 'freemium-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { isNil, pick } from 'lodash';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { ContactListType, FormServFieldType } from '../../../shared/types';
import { BulkUpdateType } from '../HeaderComponent/types';
import {
  BACK_END_FIELDS,
  CONTACT_UPDATE_SUPPORTED_FIELD_NAMES,
  CONTACT_UPDATE_SUPPORTED_FIELD_TYPES,
  CONTACT_UPDATE_UNSUPPORTED_FIELD_NAMES,
  FILTER_DATE_TIME_VALUE_FIELDS,
  FILTER_DATE_VALUE_FIELDS,
  TEMPORARY_HIDDEN_FIELDS,
  UNSUPPORTED_FIELDS_FOR_NOW,
  YUP_ARRAY_FIELD_TYPES,
  YUP_BOOLEAN_FIELD_TYPES,
  YUP_DATE_FIELD_TYPES,
  YUP_DATE_TIME_FIELD_TYPES,
  YUP_NUMBER_FIELD_TYPES,
  YUP_STRING_FIELD_TYPES,
} from '../../../shared/constants';
import { useGetContactForm } from '../../../shared/hooks';
import { FormField } from '../../../shared/styles';
import { getSchemaFromFormServFields } from '../../utils';
import ContactField from '../ContactField';

type UpdateFieldsButtonProps = {
  contactLists?: ContactListType[];
  isLoading: boolean;
  bulkUpdateSelectedContacts: (data: BulkUpdateType) => void;
};

const getDefaultValue = (field: FormServFieldType): any => {
  if (field.default_value) {
    return field.default_value;
  }
  if (
    [
      ...YUP_STRING_FIELD_TYPES,
      ...YUP_DATE_FIELD_TYPES,
      ...YUP_DATE_TIME_FIELD_TYPES,
    ].includes(field.type)
  ) {
    return '';
  }
  if (YUP_NUMBER_FIELD_TYPES.includes(field.type)) {
    return null;
  }
  if (YUP_ARRAY_FIELD_TYPES.includes(field.type)) {
    return [];
  }
  if (YUP_BOOLEAN_FIELD_TYPES.includes(field.type)) {
    return false;
  }
};

const UpdateFieldsButton = (props: UpdateFieldsButtonProps) => {
  const { t, i18n } = useTranslation();
  const { contactLists, bulkUpdateSelectedContacts, isLoading } = props;
  const [currentValidation, setCurrentValidation] =
    React.useState<Record<string, Yup.AnySchema>>();
  const [selectedField, setSelectedField] =
    React.useState<FormServFieldType | null>(null);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [fields, setFields] = React.useState<FormServFieldType[]>([]);
  const {
    data: contactForm,
    isLoading: isLoadingContactForm,
    isError: isContactFormError,
  } = useGetContactForm();

  const validations = React.useMemo(
    () => getSchemaFromFormServFields(fields),
    [fields],
  );

  const form = useForm({
    mode: 'onChange',
    resolver: yupResolver(Yup.object(currentValidation).required()),
  });

  const { handleSubmit, setValue } = form;

  React.useEffect(() => {
    // bulk update does not work for multiselect field
    // Decided to block updation of certain fields according to v3 PRD
    const newFields =
      contactForm?.fields?.filter(
        (field: FormServFieldType) =>
          (field.visible &&
            ![...TEMPORARY_HIDDEN_FIELDS, ...BACK_END_FIELDS].includes(
              field.name,
            ) &&
            ![
              ...UNSUPPORTED_FIELDS_FOR_NOW,
              ...FILTER_DATE_TIME_VALUE_FIELDS,
              ...FILTER_DATE_VALUE_FIELDS,
            ].includes(field.type) &&
            CONTACT_UPDATE_SUPPORTED_FIELD_TYPES.includes(field.type) &&
            !CONTACT_UPDATE_UNSUPPORTED_FIELD_NAMES.includes(field.name)) ||
          CONTACT_UPDATE_SUPPORTED_FIELD_NAMES.includes(field.name),
      ) || [];
    setFields(newFields);
  }, [contactForm]);

  const onSubmit = (data: Record<string, any>) => {
    if (!selectedField) return;
    bulkUpdateSelectedContacts({
      fields: data,
      callback: (isSuccess: boolean) => {
        if (isSuccess) {
          setIsOpen(false);
          setSelectedField(null);
          form.reset({});
        }
      },
    });
  };

  if (isLoadingContactForm) return <Loader size="small" />;

  if (isContactFormError) return null;

  return (
    <>
      <Button
        type="secondary"
        inline
        aria-describedby="contact-lists-dropdown"
        onClick={() => setIsOpen(true)}
      >
        {t('update_field')}
      </Button>
      <Confirmation
        width="540px"
        isOpen={isOpen}
        onDismiss={(type) => {
          if (type === 'overlay') return;
          setIsOpen(false);
          setSelectedField(null);
          form.reset({});
        }}
        title={`${t('update_field')}`}
        cancelButtonDisabled={isLoading}
        confirmButtonDisabled={isNil(selectedField) || isLoading}
        cancelButtonText={`${t('cancel')}`}
        confirmButtonText={`${t('update_field')}`}
        onConfirm={() =>
          handleSubmit(onSubmit, (err) => console.log('ERR', err))()
        }
      >
        <FormField>
          <Dropdown
            id="modal-dd-update-field"
            defaultSelectedItem={selectedField}
            label={`${t('forms.labels.field_name')}`}
            type="input"
            filterKey="label"
            emptyItemsText={`${t('no_results_found')}`}
            placeholder={`${t('forms.placeholders.search_field_name')}`}
            itemToString={(v: FormServFieldType) =>
              i18n.exists(
                `contacts.import_contact.default_fields.contact.${v.name}`,
              )
                ? `${t(
                    `contacts.import_contact.default_fields.contact.${v.name}`,
                  )}`
                : v.label || ''
            }
            itemComponent={(v: FormServFieldType) =>
              i18n.exists(
                `contacts.import_contact.default_fields.contact.${v.name}`,
              )
                ? `${t(
                    `contacts.import_contact.default_fields.contact.${v.name}`,
                  )}`
                : v.label || ''
            }
            isErrored={false}
            errorText=""
            items={fields}
            onChange={(option: FormServFieldType) => {
              if (!option) return;
              if (selectedField?.name) setValue(selectedField.name, undefined);
              setSelectedField(option);
              setValue(option.name, getDefaultValue(option));
              setCurrentValidation(pick(validations, [option.name]));
            }}
          />
        </FormField>
        {selectedField && (
          <FormField>
            <ContactField
              setFormValue={(n, v: any) => setValue(n, v)}
              field={selectedField}
              form={form}
              contactLists={contactLists || []}
              autoFocus
            />
          </FormField>
        )}
      </Confirmation>
    </>
  );
};

export default UpdateFieldsButton;
